<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { PublicEventData, PublicEventRace } from '@/types/events'
import type { LegalDocumentKey } from '@/types/legal'
import { fetchPublicEventRaces } from '@/api/events'
import { sendPageView } from '@/api/pageViews'
import { useParticipantAuth } from '@/composables/useParticipantAuth'
import { setLocale, SUPPORTED } from '@/i18n'
import LanguageToggle from '@/components/public/LanguageToggle.vue'
import LegalDrawer from '@/components/LegalDrawer.vue'
import OrganiserLogo from '@/components/shared/OrganiserLogo.vue'
import RacePriceDisplay from '@/components/shared/RacePriceDisplay.vue'
import RaceTierPill from '@/components/shared/RaceTierPill.vue'
import { buildStartlineTermsHtml } from '@/utils/startlineTermsContent'
import { buildStartlinePrivacyHtml } from '@/utils/startlinePrivacyContent'
import { useAppConfig } from '@/composables/useAppConfig'
import { venueNameCityLine } from '@/utils/venueLabel'
import { buildGoogleMapsUrl } from '@/utils/googleMapsUrl'
import { FALLBACK_COMPANY } from '@/constants/company'

// Already resolved by the backend (race venue, else edition venue, else null).
function startVenueLine(race: PublicEventRace): string | null {
  return venueNameCityLine(race.start_venue)
}

function startVenueMapsUrl(race: PublicEventRace): string | null {
  return race.start_venue ? buildGoogleMapsUrl(race.start_venue) : null
}

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { participantUser, isParticipantAuthenticated, initParticipantAuth } = useParticipantAuth()
const { config } = useAppConfig()

const termsHtml = computed(() =>
  buildStartlineTermsHtml(
    config.value?.email ?? { info: 'info@startline.be' },
    config.value?.company ?? FALLBACK_COMPANY,
  ),
)
const privacyHtml = computed(() =>
  buildStartlinePrivacyHtml(
    config.value?.email ?? { support: 'support@startline.be', info: 'info@startline.be' },
    config.value?.company ?? FALLBACK_COMPANY,
  ),
)

const orgSlug = String(route.params['orgSlug'])
const eventSlug = String(route.params['eventSlug'])

const eventData = ref<PublicEventData | null>(null)
const isLoading = ref<boolean>(true)
const loadError = ref<boolean>(false)

const legalDrawerOpen = ref<boolean>(false)
const legalDrawerDocument = ref<LegalDocumentKey>('race_rules')
const returnFocusEl = ref<HTMLElement | null>(null)

const websiteUrl = computed<string | null>(() => eventData.value?.event.website_url ?? null)
const raceRules = computed<string | null>(() => eventData.value?.edition.race_rules ?? null)
const termsAndConditions = computed<string | null>(() => eventData.value?.edition.terms_and_conditions ?? null)
const hasRules = computed<boolean>(() => raceRules.value !== null && raceRules.value.trim() !== '')
const hasTerms = computed<boolean>(() => termsAndConditions.value !== null && termsAndConditions.value.trim() !== '')
// Footer legal links crowd on mobile only once all four are present (organizer
// documents + the two always-present Startline links) - split into two rows
// only then, otherwise keep the natural single-row layout.
const footerCrowded = computed<boolean>(() => hasRules.value && hasTerms.value)

function openLegalDrawer(doc: LegalDocumentKey, trigger: HTMLElement): void {
  returnFocusEl.value = trigger
  legalDrawerDocument.value = doc
  legalDrawerOpen.value = true
}

onMounted(async () => {
  // Captured before the ?lang cleanup below so UTM values are read from the entry URL
  const entryQuery = { ...route.query }

  // Apply ?lang param from the event-site handoff, then clean it from the URL
  const langParam = route.query['lang']
  if (typeof langParam === 'string' && (SUPPORTED as readonly string[]).includes(langParam)) {
    setLocale(langParam)
    const { lang: _removed, ...restQuery } = route.query
    void router.replace({ query: restQuery })
  }

  await Promise.all([
    fetchPublicEventRaces(orgSlug, eventSlug).then((data) => {
      eventData.value = data
      sendPageView(
        { type: 'page_view.event_page', org_slug: orgSlug, event_slug: eventSlug },
        entryQuery,
      )
    }).catch(() => {
      loadError.value = true
    }),
    initParticipantAuth(),
  ])
  isLoading.value = false
})

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function formatTime(dateStr: string): string {
  // Extract HH:mm directly from ISO string without timezone conversion
  // ISO format: "YYYY-MM-DDTHH:MM:SS..." - we want characters 11-15 (HH:MM)
  return dateStr.slice(11, 16)
}

function formatDistance(distance: string): string {
  const val = parseFloat(distance)
  return `${val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)} km`
}

function registrationUrl(race: PublicEventRace): string {
  return `/${orgSlug}/${eventSlug}/${race.slug}`
}

function capacityPercent(race: PublicEventRace): number {
  if (!race.max_participants) return 0
  return Math.min(100, (race.registrations_count / race.max_participants) * 100)
}

function capacityBarClass(race: PublicEventRace): string {
  const pct = capacityPercent(race)
  if (pct >= 90) return 'bg-red-400'
  if (pct >= 75) return 'bg-amber-400'
  return 'bg-emerald-400'
}
</script>

<template>
  <div class="min-h-screen bg-slate-50 flex flex-col">
    <!-- Loading state -->
    <div v-if="isLoading" class="flex items-center justify-center min-h-screen">
      <svg class="animate-spin h-10 w-10 text-emerald-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>

    <!-- Error: not found -->
    <div
      v-else-if="loadError || !eventData"
      class="flex items-center justify-center min-h-screen px-4"
    >
      <div class="max-w-md w-full text-center bg-white rounded-2xl border border-slate-200 p-10">
        <div class="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <svg class="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 class="text-xl font-bold text-slate-900 mb-2">{{ t('event_page.not_found') }}</h1>
        <a href="/" class="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors mt-4">
          {{ t('common.back') }}
        </a>
      </div>
    </div>

    <!-- Event page -->
    <template v-else>
      <!-- Page header -->
      <div class="bg-white border-b border-slate-200">
        <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:pt-6 sm:pb-3">
          <div class="flex items-start justify-between gap-3 sm:gap-4">
            <!-- Identity: logo + title -->
            <div class="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
              <!-- Event logo -->
              <img
                v-if="eventData.event.logo_url"
                :src="eventData.event.logo_url"
                :alt="eventData.event.name"
                class="w-14 h-14 rounded-xl object-cover border border-slate-200 shrink-0 sm:w-20 sm:h-20"
              />
              <div
                v-else
                class="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center shrink-0 sm:w-20 sm:h-20"
              >
                <svg class="w-7 h-7 text-slate-400 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>

              <div class="min-w-0 flex-1">
                <h1 class="text-xl font-bold text-slate-900 leading-tight sm:text-3xl mb-1">
                  {{ eventData.event.name }}
                  <a
                    v-if="websiteUrl"
                    :href="websiteUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex align-middle text-emerald-600 hover:text-emerald-700 transition-colors"
                    :aria-label="t('common.visit_event_website')"
                    :title="t('common.visit_event_website')"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </h1>
                <p class="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  {{ eventData.edition.name }}
                </p>
              </div>
            </div>

            <!-- Right-side controls: language toggle + participant welcome.
                 Signed out, the column holds only the compact dropdown
                 trigger and does not need the wider signed-in width. -->
            <div
              class="flex shrink-0 flex-col items-end gap-1.5 sm:w-auto sm:gap-2 sm:self-center sm:pl-4"
              :class="isParticipantAuthenticated ? 'w-28' : 'w-16'"
            >
              <LanguageToggle />
              <template v-if="isParticipantAuthenticated">
                <div class="flex w-full flex-col items-end gap-1.5 sm:w-auto sm:gap-2">
                  <span class="w-full truncate text-right text-xs text-slate-600 sm:w-auto sm:text-sm">
                    {{ t('nav.welcome', { name: participantUser?.first_name }) }}
                  </span>
                  <a
                    href="/my/races"
                    class="inline-flex w-full items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg px-2 py-1.5 text-xs leading-tight transition-colors sm:w-auto sm:whitespace-nowrap sm:px-4 sm:py-2 sm:text-sm"
                  >
                    {{ t('nav.my_dashboard') }}
                  </a>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Two-column layout -->
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="lg:grid lg:grid-cols-3 lg:gap-8">
          <!-- Races column (2/3) -->
          <div class="lg:col-span-2">
            <h2 class="text-lg font-semibold text-slate-900 mb-4">
              {{ t('event_page.races_heading') }}
            </h2>

            <!-- No races -->
            <div
              v-if="eventData.races.length === 0"
              class="text-center bg-white rounded-2xl border border-slate-200 p-10"
            >
              <p class="text-sm text-slate-500">{{ t('event_page.no_races') }}</p>
            </div>

            <!-- Race cards -->
            <div class="space-y-4">
              <div
                v-for="race in eventData.races"
                :key="race.id"
                class="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors"
              >
                <div class="px-6 py-5">
                  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <!-- Race info -->
                    <div class="min-w-0 flex-1">
                      <div class="flex flex-wrap items-center gap-2 mb-1">
                        <h3 class="text-lg font-semibold text-slate-900">
                          {{ race.name }}
                        </h3>
                        <!-- Type badge -->
                        <span class="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 capitalize">
                          {{ race.type }}
                        </span>
                        <!-- Status badge -->
                        <span
                          v-if="race.is_closed"
                          class="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700"
                        >
                          {{ race.registration_availability === 'not_yet_open' ? t('event_page.not_yet_open_badge') : t('event_page.closed_badge') }}
                        </span>
                        <template v-else>
                          <span class="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                            {{ t('event_page.open_badge') }}
                          </span>
                          <span
                            v-if="race.is_waiting_list_active"
                            class="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700"
                          >
                            {{ t('registration.waiting_list.badge') }}
                          </span>
                        </template>
                        <!-- Pricing tier badge with popover -->
                        <RaceTierPill :pricing="race.pricing" />
                      </div>

                      <!-- Race details -->
                      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
                        <!-- Distance -->
                        <span v-if="parseFloat(race.distance) > 0" class="inline-flex items-center gap-1">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24" aria-hidden="true">
                            <circle cx="6" cy="19" r="3" />
                            <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
                            <circle cx="18" cy="5" r="3" />
                          </svg>
                          {{ formatDistance(race.distance) }}
                        </span>
                        <!-- Elevation -->
                        <span v-if="race.elevation" class="inline-flex items-center gap-1">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                          </svg>
                          {{ race.elevation }} m {{ t('event_page.elevation_label') }}
                        </span>
                        <!-- Start time -->
                        <span class="inline-flex items-center gap-1">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {{ formatDate(race.start_time) }} - {{ formatTime(race.start_time) }}
                        </span>
                        <!-- Start venue: always on its own line, w-full forces the wrap -->
                        <span v-if="startVenueLine(race)" class="flex w-full items-center gap-1 mt-1">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {{ startVenueLine(race) }}
                          <a
                            v-if="startVenueMapsUrl(race)"
                            :href="startVenueMapsUrl(race)!"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex items-center text-emerald-600 hover:text-emerald-700 transition-colors"
                            :aria-label="t('common.open_in_google_maps')"
                            :title="t('common.open_in_google_maps')"
                          >
                              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                          </a>
                        </span>
                      </div>

                      <!-- Capacity bar -->
                      <div v-if="race.max_participants" class="mt-3">
                        <div class="flex items-center justify-between mb-1">
                          <span class="text-xs text-slate-400">
                            {{ t('event_page.spots_taken', { taken: race.registrations_count, max: race.max_participants }) }}
                          </span>
                        </div>
                        <div class="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                          <div
                            class="h-full rounded-full transition-all"
                            :class="capacityBarClass(race)"
                            :style="{ width: `${capacityPercent(race)}%` }"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Price + Register button -->
                    <div class="flex flex-col gap-2 shrink-0 w-40 self-center sm:self-auto sm:ml-6">
                      <RacePriceDisplay :pricing="race.pricing" variant="full" hide-tier-pill />
                      <a
                        v-if="!race.is_closed"
                        :href="registrationUrl(race)"
                        class="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors whitespace-nowrap"
                      >
                        {{ race.is_waiting_list_active ? t('registration.waiting_list.join_button') : t('event_page.register_button') }}
                      </a>
                      <span
                        v-else
                        class="inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-400 font-semibold rounded-lg px-5 py-2.5 text-sm cursor-not-allowed whitespace-nowrap"
                      >
                        {{ t('event_page.register_button') }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Organiser sidebar (1/3, sticky, desktop only) -->
          <div
            v-if="eventData.organiser.name || eventData.organiser.logo_url"
            class="hidden lg:block"
          >
            <!-- Spacer matching the races <h2> + mb-4 so the card aligns with the first race card -->
            <div class="h-7 mb-4" aria-hidden="true"></div>
            <div class="sticky top-8">
              <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden p-5">
                <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  {{ t('event_page.organised_by') }}
                </h3>
                <div class="flex items-center gap-3">
                  <OrganiserLogo :src="eventData.organiser.logo_url" :alt="eventData.organiser.name ?? ''" />
                  <p class="font-semibold text-slate-900 text-sm leading-tight">
                    {{ eventData.organiser.name }}
                  </p>
                </div>
                <div class="mt-3 space-y-1.5">
                  <a
                    v-if="eventData.organiser.website"
                    :href="eventData.organiser.website"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 transition-colors"
                  >
                    <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    {{ eventData.organiser.website }}
                  </a>
                  <a
                    v-if="eventData.organiser.email"
                    :href="`mailto:${eventData.organiser.email}`"
                    class="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 transition-colors"
                  >
                    <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {{ eventData.organiser.email }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile organiser card (below races) -->
        <div
          v-if="eventData.organiser.name || eventData.organiser.logo_url"
          class="lg:hidden mt-6"
        >
          <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden p-5">
            <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              {{ t('event_page.organised_by') }}
            </h3>
            <div class="flex items-center gap-3">
              <OrganiserLogo :src="eventData.organiser.logo_url" :alt="eventData.organiser.name ?? ''" />
              <p class="font-semibold text-slate-900 text-sm leading-tight">
                {{ eventData.organiser.name }}
              </p>
            </div>
            <div class="mt-3 space-y-1.5">
              <a
                v-if="eventData.organiser.website"
                :href="eventData.organiser.website"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                {{ t('event_page.organiser_website') }}
              </a>
              <a
                v-if="eventData.organiser.email"
                :href="`mailto:${eventData.organiser.email}`"
                class="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {{ t('event_page.organiser_contact') }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="border-t border-slate-200 py-4 mt-8">
        <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p class="text-center text-xs text-slate-400">
            {{ t('nav.powered_by') }}
            <a
              href="https://startline.be"
              target="_blank"
              rel="noopener noreferrer"
              class="font-black text-slate-500 hover:text-slate-700 transition-colors"
            >Startline<span class="inline-block w-1 h-1 rounded-full bg-emerald-400 ml-0.5 align-middle"></span></a>
          </p>
          <div
            class="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-slate-400 sm:flex-nowrap sm:justify-start sm:gap-4"
          >
            <button
              v-if="hasRules"
              type="button"
              class="hover:text-slate-600 transition-colors bg-transparent border-0 p-0 cursor-pointer"
              @click="openLegalDrawer('race_rules', $event.currentTarget as HTMLElement)"
            >{{ t('footer.race_rules') }}</button>
            <button
              v-if="hasTerms"
              type="button"
              class="hover:text-slate-600 transition-colors bg-transparent border-0 p-0 cursor-pointer"
              @click="openLegalDrawer('terms_and_conditions', $event.currentTarget as HTMLElement)"
            >{{ t('footer.event_terms') }}</button>
            <!-- Zero-height spacer that forces the two organizer-document
                 buttons above onto their own row on mobile. Relies on DOM
                 order alone (it sits between them and the two Startline
                 links below) - no order-* utilities needed here or on the
                 buttons around it. -->
            <span
              v-if="footerCrowded"
              class="-mt-1.5 h-0 basis-full sm:hidden"
              aria-hidden="true"
            ></span>
            <button
              type="button"
              class="hover:text-slate-600 transition-colors bg-transparent border-0 p-0 cursor-pointer"
              @click="openLegalDrawer('privacy_policy', $event.currentTarget as HTMLElement)"
            >{{ t('footer.privacy_policy') }}</button>
            <button
              type="button"
              class="hover:text-slate-600 transition-colors bg-transparent border-0 p-0 cursor-pointer"
              @click="openLegalDrawer('startline_terms', $event.currentTarget as HTMLElement)"
            >{{ t('footer.startline_terms') }}</button>
          </div>
        </div>
      </div>
    </template>

    <LegalDrawer
      v-model:open="legalDrawerOpen"
      :initial-document="legalDrawerDocument"
      :race-rules="raceRules"
      :terms-and-conditions="termsAndConditions"
      :startline-terms="termsHtml"
      :privacy-policy="privacyHtml"
      :return-focus-to="returnFocusEl"
    />
  </div>
</template>
